import { useState, type FormEvent } from 'react';
import { useForm, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { LEAD_FORM_FIELDS, type LeadFieldConfig } from '@/lib/leadForm';
import { newEventId, readPixelCookies, trackLead } from '@/lib/metaPixel';
import { hasTrackingConsent } from '@/lib/consent';
import { ThankYouModal } from './ThankYouModal';

type Status = 'idle' | 'error';

interface CoreFields {
  name: string;
  email: string;
  phone: string;
  description: string;
}

// Custom-field values keyed by `field.key`. Stored loose in the form
// so react-hook-form can register them dynamically.
type CustomValues = Record<string, string>;

interface FormValues extends CoreFields {
  custom: CustomValues;
  /** Honeypot — must remain empty. Hidden from real users via CSS. */
  website: string;
}

interface CustomFieldPayload {
  key: string;
  label: string;
  value: string;
}

/**
 * Meta Conversions API context. Sent only when the visitor has accepted
 * the cookie banner; the server skips the server-side conversion when
 * this is absent, so declining really does mean no tracking either side.
 */
interface MetaPayload {
  eventId: string;
  fbp?: string;
  fbc?: string;
  sourceUrl?: string;
}

interface SubmitPayload extends CoreFields {
  customFields: CustomFieldPayload[];
  website: string;
  meta?: MetaPayload;
}

/**
 * Builds the dedup context for a submission, or undefined if the visitor
 * hasn't consented. The event ID is minted here — before the request —
 * so the server event and the browser event carry the identical ID and
 * Meta collapses them into one conversion.
 */
function buildMetaPayload(): MetaPayload | undefined {
  if (!hasTrackingConsent()) return undefined;
  const { fbp, fbc } = readPixelCookies();
  return {
    eventId: newEventId(),
    fbp,
    fbc,
    sourceUrl: typeof window === 'undefined' ? undefined : window.location.href,
  };
}

const EMPTY_CUSTOM: CustomValues = LEAD_FORM_FIELDS.reduce<CustomValues>((acc, f) => {
  acc[f.key] = '';
  return acc;
}, {});

function findCustomValue(fields: CustomFieldPayload[], key: string): string | undefined {
  return fields.find((f) => f.key === key)?.value;
}

function buildCustomFieldsPayload(values: CustomValues): CustomFieldPayload[] {
  return LEAD_FORM_FIELDS.flatMap((field) => {
    const raw = values[field.key] ?? '';
    if (raw === '') return [];
    return [{ key: field.key, label: field.label, value: raw }];
  });
}

interface FieldProps {
  field: LeadFieldConfig;
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
}

function CustomFieldInput({ field, register, errors }: FieldProps): React.JSX.Element {
  const errMsg = errors.custom?.[field.key]?.message;
  const id = `lead-${field.key}`;
  const required = field.required ?? false;

  return (
    <div className={`lead-field ${field.type === 'textarea' ? 'lead-field-full' : ''}`}>
      <label className="lead-label" htmlFor={id}>
        {field.label}
        {required && <span className="req">*</span>}
      </label>

      {field.type === 'select' ? (
        <select
          id={id}
          className="lead-select"
          {...register(`custom.${field.key}` as const, { required })}
        >
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : field.type === 'textarea' ? (
        <textarea
          id={id}
          className="lead-textarea"
          placeholder={field.placeholder}
          {...register(`custom.${field.key}` as const, { required })}
        />
      ) : (
        <input
          id={id}
          type="text"
          className="lead-input"
          placeholder={field.placeholder}
          {...register(`custom.${field.key}` as const, { required })}
        />
      )}

      {field.help && <div className="lead-help">{field.help}</div>}
      {errMsg && <div className="lead-error">{String(errMsg)}</div>}
    </div>
  );
}

/**
 * The lead-capture form. Posts to POST /api/leads with the same payload
 * shape and protections as the previous site: off-screen honeypot,
 * rate-limit-aware error copy, custom fields from lib/leadForm.ts.
 */
export function LeadForm(): React.JSX.Element {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showThanks, setShowThanks] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      description: '',
      custom: EMPTY_CUSTOM,
      website: '',
    },
  });

  const submit = useMutation({
    mutationFn: (input: SubmitPayload) =>
      api<{ id: string; ok: true }>('/leads', { method: 'POST', body: input }),
    onSuccess: (_data, input) => {
      // Meta conversion. Fired here — after the server has accepted the
      // lead — so campaigns optimise on real submissions, not on clicks
      // or on submissions the server rejected as spam or rate-limited.
      // Only the coarse budget/timeline buckets go to Meta; never the
      // name, email, phone or brief.
      trackLead({
        source: 'lead_form',
        budget: findCustomValue(input.customFields, 'budget'),
        timeline: findCustomValue(input.customFields, 'timeline'),
        // Same ID the server sends to the Conversions API, so the two
        // halves dedupe into a single conversion.
        eventId: input.meta?.eventId,
      });
      setStatus('idle');
      setErrorMsg(null);
      setShowThanks(true);
      reset();
    },
    onError: (err) => {
      setStatus('error');
      if (err instanceof ApiError && err.status === 429) {
        setErrorMsg(
          err.message || "You've submitted recently — please wait a minute and try again.",
        );
      } else if (err instanceof ApiError) {
        setErrorMsg(err.message);
      } else {
        setErrorMsg('Could not submit. Please try again.');
      }
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    setStatus('idle');
    setErrorMsg(null);
    void handleSubmit((values) => {
      submit.mutate({
        name: values.name,
        email: values.email,
        phone: values.phone,
        description: values.description,
        customFields: buildCustomFieldsPayload(values.custom),
        website: values.website,
        meta: buildMetaPayload(),
      });
    })(e);
  };

  return (
    <>
      <form className="lead-form" onSubmit={onSubmit} noValidate>
        {/*
          Honeypot field — kept off-screen (not display:none, which
          some bots detect and skip). Real users never see or focus
          it; bots that auto-fill all form inputs will fill it and
          the server will silently drop the submission.
        */}
        <div aria-hidden="true" className="lead-honeypot">
          <label htmlFor="lead-website">
            Website (leave this empty)
            <input
              id="lead-website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              {...register('website')}
            />
          </label>
        </div>

        <div className="lead-field">
          <label className="lead-label" htmlFor="lead-name">
            Name<span className="req">*</span>
          </label>
          <input
            id="lead-name"
            type="text"
            className="lead-input"
            autoComplete="name"
            {...register('name', { required: 'Required' })}
          />
          {errors.name && <div className="lead-error">{errors.name.message}</div>}
        </div>

        <div className="lead-field">
          <label className="lead-label" htmlFor="lead-email">
            Email<span className="req">*</span>
          </label>
          <input
            id="lead-email"
            type="email"
            className="lead-input"
            autoComplete="email"
            placeholder="you@yourcompany.com"
            {...register('email', {
              required: 'Required',
              pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email' },
            })}
          />
          {errors.email && <div className="lead-error">{errors.email.message}</div>}
        </div>

        <div className="lead-field">
          <label className="lead-label" htmlFor="lead-phone">
            Phone
          </label>
          <input
            id="lead-phone"
            type="tel"
            className="lead-input"
            autoComplete="tel"
            placeholder="optional"
            {...register('phone')}
          />
        </div>

        {/* Custom fields from leadForm.ts config */}
        {LEAD_FORM_FIELDS.filter((f) => f.type !== 'textarea').map((field) => (
          <CustomFieldInput key={field.key} field={field} register={register} errors={errors} />
        ))}

        <div className="lead-field lead-field-full">
          <label className="lead-label" htmlFor="lead-description">
            What should we build or automate?<span className="req">*</span>
          </label>
          <textarea
            id="lead-description"
            className="lead-textarea"
            rows={5}
            placeholder="The system you need, the process that's slow today, or the problem you want gone."
            {...register('description', { required: 'Required' })}
          />
          {errors.description && (
            <div className="lead-error">{errors.description.message}</div>
          )}
        </div>

        {/* Any textarea-typed custom fields */}
        {LEAD_FORM_FIELDS.filter((f) => f.type === 'textarea').map((field) => (
          <CustomFieldInput key={field.key} field={field} register={register} errors={errors} />
        ))}

        <div className="lead-actions">
          <button type="submit" className="btn btn-primary btn-lg" disabled={submit.isPending}>
            {submit.isPending ? 'Sending…' : 'Send project brief'}{' '}
            <span className="arrow">→</span>
          </button>
        </div>

        {status === 'error' && errorMsg && (
          <div className="lead-status error">{errorMsg}</div>
        )}

        <div className="lead-reply-note">We reply within one business day.</div>
      </form>

      <ThankYouModal open={showThanks} onClose={() => setShowThanks(false)} />
    </>
  );
}
